import React from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../content/ContentContext';
import { getIcon } from '../content/icons';

const defaultSupport = {
  eyebrow: "科研支持",
  title: "依托上海理工大学季云峰教授团队",
  desc: "课程研发依托上海理工大学季云峰教授团队在机器人、智能控制与嵌入式系统方向的科研积累，将真实工程项目转化为学校及机构可实施的项目式课程包。",
  image: "/images/site/professor-ji-support.jpg",
  alt: "教授团队科研与课程转化支持",
  highlights: [
    "高校科研团队支持",
    "真实项目转化",
    "课堂可落地"
  ],
  cards: [
    {
      src: "/images/site/professor-ji-certificate.jpg",
      title: "季云峰博士",
      desc: "上海理工大学科研团队支持，提供机器人、智能控制与嵌入式系统方向的课程转化经验。",
      type: "portrait"
    },
    {
      src: "/images/site/morse-kit-source.png",
      title: "智能硬件项目",
      desc: "把科研原型拆解成机构可实施的触摸输入、编码反馈和屏幕交互任务。"
    },
    {
      src: "/images/site/ball-balance-device.jpg",
      title: "控制系统转化",
      desc: "围绕传感器、舵机与反馈调节，让抽象控制概念有可观察的课堂结果。"
    },
    {
      src: "/images/site/classroom-close.jpg",
      title: "课堂落地支持",
      desc: "从设备配置、课堂流程到老师培训，帮助学校把项目课稳定搬进课堂。"
    }
  ]
};

const defaultSupportCards = defaultSupport.cards;

export default function About() {
  const { about, home } = useContent();
  const { hero, story, values, team } = about;
  const support = about.support || defaultSupport;
  const supportCards = support.cards?.length ? support.cards : defaultSupportCards;
  const supportHighlights = support.highlights?.length ? support.highlights : defaultSupport.highlights;
  const proof = home.proof || {};
  const proofItems = proof.items || [];

  return (
    <div>
      {/* 页头 */}
      <section className="page-top pb-12 md:pb-16">
        <div className="container-x text-center">
          <div className="eyebrow mb-3 reveal">{hero.eyebrow}</div>
          <h1 className="text-4xl md:text-6xl font-semibold tracking-tight reveal" style={{ color: 'var(--ink)', transitionDelay: '0.05s' }}>
            {hero.title}
          </h1>
        </div>
      </section>

      {/* 品牌故事 */}
      <section className="pb-24 md:pb-32">
        <div className="container-x mx-auto max-w-2xl space-y-8 reveal">
          {(story || []).map((p, i) => (
            <p key={i} className="text-xl md:text-2xl leading-relaxed font-light" style={{ color: '#3a3a3c' }}>{p}</p>
          ))}
        </div>
      </section>

      {/* 我们相信什么 */}
      <section className="section-alt py-20 md:py-28">
        <div className="container-x w-full">
          <div className="grid gap-10 lg:grid-cols-[0.88fr_1.12fr] lg:items-center">
            <div className="reveal">
              <div className="eyebrow mb-4">{support.eyebrow || defaultSupport.eyebrow}</div>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>{support.title || defaultSupport.title}</h2>
              <p className="mt-6 max-w-2xl text-[16px] leading-relaxed" style={{ color: 'var(--body)' }}>
                {support.desc || defaultSupport.desc}
              </p>
              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                {supportHighlights.map((item) => (
                  <div key={item} className="support-stat">
                    <span />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="support-gallery reveal">
              <div className="support-portrait-card">
                <img src={supportCards[0].src} alt={supportCards[0].title} />
                <div>
                  <h3>{supportCards[0].title}</h3>
                  <p>{supportCards[0].desc}</p>
                </div>
              </div>
              <div className="support-mini-grid">
                {supportCards.slice(1).map((item, i) => (
                  <div key={item.src} className="support-mini-card" style={{ transitionDelay: `${(i + 1) * 0.08}s` }}>
                    <img src={item.src} alt={item.title} />
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {proofItems.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="container-x">
            <div className="max-w-2xl reveal">
              <div className="eyebrow mb-3">{proof.eyebrow || "课堂现场"}</div>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>{proof.title || "从真实项目走进真实课堂"}</h2>
              <p className="mt-5 text-[16px] leading-relaxed" style={{ color: 'var(--body)' }}>{proof.desc || "课程包围绕摩斯电码、电阻屏交互与板球平衡等项目展开，支持机构组织搭建、调试和展示。"}</p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {proofItems.map((item, i) => (
                <div key={item.src || i} className="reveal">
                  <img src={item.src} alt={item.title} className="aspect-[16/10] w-full object-contain bg-white" style={{ borderRadius: '12px' }} />
                  <h3 className="mt-4 text-[18px] font-semibold" style={{ color: 'var(--ink)' }}>{item.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed" style={{ color: 'var(--muted)' }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section-alt py-24 md:py-32">
        <div className="container-x">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight reveal" style={{ color: 'var(--ink)' }}>{values.title}</h2>
          <div className="mt-16 grid gap-x-12 gap-y-14 md:grid-cols-3">
            {values.items.map((v, i) => {
              const Icon = getIcon(v.icon);
              return (
                <div key={i} className="reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                  <Icon className="h-8 w-8" strokeWidth={1.5} style={{ color: 'var(--ink)' }} />
                  <h3 className="mt-5 text-xl font-semibold" style={{ color: 'var(--ink)' }}>{v.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed" style={{ color: 'var(--muted)' }}>{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 团队 */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight reveal" style={{ color: 'var(--ink)' }}>{team.title}</h2>
          <p className="mt-4 text-lg reveal" style={{ color: 'var(--muted)' }}>{team.subtitle}</p>
          <div className="mt-16 grid gap-x-12 gap-y-16 sm:grid-cols-3">
            {team.members.map((m, i) => (
              <div key={i} className="reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                {m.image ? (
                  <img src={m.image} alt={m.name} className="h-20 w-20 object-cover" style={{ borderRadius: '16px' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center text-2xl font-semibold"
                    style={{ borderRadius: '16px', background: 'var(--bg-alt)', color: 'var(--ink)' }}>
                    {(m.name || ' ').charAt(0)}
                  </div>
                )}
                <h3 className="mt-5 text-xl font-semibold" style={{ color: 'var(--ink)' }}>{m.name}</h3>
                <div className="text-[14px]" style={{ color: 'var(--muted)' }}>{m.role}</div>
                <p className="mt-3 text-[15px] leading-relaxed" style={{ color: 'var(--muted)' }}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-alt py-24 md:py-32 text-center">
        <div className="container-x reveal">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>想更近距离了解？</h2>
          <p className="mt-5 text-lg" style={{ color: 'var(--muted)' }}>获取课程包、硬件套件和机构合作方案。</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn btn-blue">获取合作方案</Link>
            <Link to="/schools" className="btn btn-white">学校合作</Link>
          </div>
        </div>
      </section>
    </div>
  );
}